/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Save, RefreshCw, Percent, DollarSign, TrendingUp, Users, Trash2, AlertTriangle, Ban, Undo2 } from 'lucide-react';
import { useAdmin } from './AdminAuth';

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MetricCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-[#0f1712]/10 bg-white p-6">
    <div className="w-11 h-11 rounded-xl bg-[#0e9f6e]/12 flex items-center justify-center mb-4">
      <Icon size={20} className="text-[#0e9f6e]" />
    </div>
    <p className="font-display text-3xl font-700 text-[#0f1712]">{value}</p>
    <p className="text-sm text-[#0f1712]/50 mt-1">{label}</p>
  </div>
);

const MarketplacePanel = () => {
  const { authAxios } = useAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feePct, setFeePct] = useState('1.5');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [del, setDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const toggleStatus = async (s) => {
    setTogglingId(s.id);
    try {
      await authAxios.post(`/admin/sellers/${s.id}/status`, { disabled: !s.disabled });
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to update status.');
      setTimeout(() => setMsg(''), 4000);
    } finally { setTogglingId(null); }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await authAxios.delete(`/admin/sellers/${del.id}`);
      setDel(null);
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to delete seller.');
      setTimeout(() => setMsg(''), 4000);
    } finally { setDeleting(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await authAxios.get('/admin/marketplace');
      setData(res.data);
      setFeePct((res.data.fee_rate * 100).toString());
    } catch (e) { /* interceptor */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveFee = async () => {
    const pct = parseFloat(feePct);
    if (isNaN(pct) || pct < 0 || pct > 20) { setMsg('Enter a value between 0 and 20.'); setTimeout(() => setMsg(''), 3500); return; }
    setSaving(true); setMsg('');
    try {
      await authAxios.put('/admin/settings/fee', { fee_rate: pct / 100 });
      setMsg('Saved! New fee applies to every future payment.');
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to save.');
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 4000);
    }
  };

  if (loading) return <div className="p-10 text-center text-[#0f1712]/40 text-sm">Loading…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl font-700 text-[#0f1712]">Marketplace</h2>
          <p className="text-[#0f1712]/50 text-sm mt-1">Your platform earnings and fee settings.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 rounded-xl border border-[#0f1712]/15 px-4 py-2.5 text-sm font-semibold text-[#0f1712] hover:border-[#0e9f6e] transition-colors">
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      {/* Fee setting */}
      <div className="rounded-2xl border border-[#0f1712]/10 bg-white p-6 mb-6">
        <h3 className="font-display text-lg font-700 text-[#0f1712] mb-1">Platform fee</h3>
        <p className="text-sm text-[#0f1712]/55 mb-4">This percentage is automatically deducted from every payment and sent to your FLIX Stripe account.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-semibold text-[#0f1712]/60 mb-1.5">Fee percentage</label>
            <div className="relative">
              <input value={feePct} onChange={(e) => setFeePct(e.target.value)} type="number" step="0.1" min="0" max="20"
                className="w-40 rounded-xl border border-[#0f1712]/15 pl-4 pr-9 py-3 text-sm outline-none focus:border-[#0e9f6e] focus:ring-2 focus:ring-[#0e9f6e]/30 transition" />
              <Percent size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0f1712]/40" />
            </div>
          </div>
          <button onClick={saveFee} disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-[#0e9f6e] text-white px-5 py-3 text-sm font-semibold hover:bg-[#0c8a60] transition-colors disabled:opacity-60">
            <Save size={15} /> {saving ? 'Saving…' : 'Save fee'}
          </button>
          {msg && <span className="text-sm text-[#0e9f6e] font-medium pb-2">{msg}</span>}
        </div>
        <div className="flex gap-2 mt-4">
          {['1.5', '2', '2.5', '3'].map((p) => (
            <button key={p} onClick={() => setFeePct(p)} className="rounded-lg border border-[#0f1712]/12 px-3 py-1.5 text-xs font-semibold text-[#0f1712]/70 hover:border-[#0e9f6e] hover:text-[#0e9f6e] transition-colors">
              {p}%
            </button>
          ))}
        </div>
      </div>

      {/* Earnings */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <MetricCard icon={DollarSign} label="Your total fees earned" value={money(data.total_fees)} />
        <MetricCard icon={TrendingUp} label="Total volume processed" value={money(data.total_volume)} />
        <MetricCard icon={Users} label="Connected sellers" value={(data.sellers || []).length} />
      </div>

      {/* Sellers */}
      <div className="rounded-2xl border border-[#0f1712]/10 overflow-hidden bg-white">
        {(!data.sellers || data.sellers.length === 0) ? (
          <div className="p-12 text-center text-[#0f1712]/45 text-sm">No sellers have joined yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#f6f6f2] text-[#0f1712]/50 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Seller</th>
                <th className="text-left px-5 py-3 font-semibold">Email</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-right px-5 py-3 font-semibold">Balance</th>
                <th className="text-right px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.sellers.map((s) => (
                <tr key={s.id} className="border-t border-[#0f1712]/8">
                  <td className="px-5 py-3.5 text-[#0f1712] font-medium">{s.name}</td>
                  <td className="px-5 py-3.5 text-[#0f1712]/60">{s.email}</td>
                  <td className="px-5 py-3.5">
                    {s.disabled ? (
                      <span className="inline-block text-xs font-semibold text-[#d64545] bg-[#fbe9e9] rounded-full px-2.5 py-1">Disabled</span>
                    ) : s.charges_enabled ? (
                      <span className="inline-block text-xs font-semibold text-[#0e9f6e] bg-[#e8f5ee] rounded-full px-2.5 py-1">Active</span>
                    ) : (
                      <span className="inline-block text-xs font-semibold text-[#0f1712]/50 bg-[#f6f6f2] rounded-full px-2.5 py-1">Pending</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-right font-display font-700 text-[#0f1712]">{money(s.balance)}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`admin-toggle-seller-${s.id}`} onClick={() => toggleStatus(s)} disabled={togglingId === s.id}
                        title={s.disabled ? 'Restore seller' : 'Disable seller'}
                        className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors disabled:opacity-50 ${s.disabled ? 'border-[#0e9f6e]/30 text-[#0e9f6e] hover:bg-[#0e9f6e]/10' : 'border-[#f0b429]/40 text-[#b7791f] hover:bg-[#f0b429]/10'}`}>
                        {s.disabled ? <Undo2 size={16} /> : <Ban size={16} />}
                      </button>
                      <button data-testid={`admin-delete-seller-${s.id}`} onClick={() => setDel(s)} title="Delete seller"
                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-[#d64545]/25 text-[#d64545] hover:bg-[#d64545]/10 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {del && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => !deleting && setDel(null)} />
          <div className="relative bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="w-12 h-12 rounded-xl bg-[#d64545]/12 flex items-center justify-center mb-4"><AlertTriangle size={22} className="text-[#d64545]" /></div>
            <h3 className="font-display text-xl font-700 text-[#0f1712]">Delete {del.name}?</h3>
            <p className="text-sm text-[#0f1712]/60 mt-2">This permanently removes the seller, their customers and transaction history from FLIX. This cannot be undone.</p>
            {del.balance > 0 && (
              <div className="mt-3 flex items-start gap-2 rounded-xl bg-[#fff4e5] border border-[#f0b429]/30 p-3 text-xs text-[#8a5a00]">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                <span>This seller has an unwithdrawn balance of <b>{money(del.balance)}</b>. Their Stripe connected account stays in Stripe and is not deleted.</span>
              </div>
            )}
            <div className="flex gap-2 mt-5">
              <button onClick={() => setDel(null)} disabled={deleting} className="flex-1 rounded-xl border border-[#0f1712]/15 py-3 text-sm font-semibold text-[#0f1712] hover:bg-[#f6f6f2] transition-colors">Cancel</button>
              <button data-testid="admin-confirm-delete-seller" onClick={confirmDelete} disabled={deleting} className="flex-1 rounded-xl bg-[#d64545] text-white py-3 text-sm font-semibold hover:bg-[#c13a3a] transition-colors disabled:opacity-60">
                {deleting ? 'Deleting…' : 'Delete seller'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplacePanel;
